/**
 * Modal that allows for adding an existing item to a cart.
 */

import { useCallback, useState } from 'react';

import { useAuth } from '../lib/auth';
import { DeliveryItem, DeliveryType } from '../lib/types';
import { DismissButton, MainButton } from './buttons';
import FadeImage from './fadeImage';
import { Counter, Divider } from './general';
import { Popup } from './modal';

interface ItemProps {
  item?: DeliveryItem;
  onAdd: (item: DeliveryItem, description: string, quantity: number) => void;
  onDismiss: () => void;
  showing: boolean;
}

const Item: React.FC<ItemProps> = ({ item, onAdd, onDismiss, showing }) => {
  const { deliveryType } = useAuth();
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState(1);

  const onConfirm = useCallback(() => {
    onAdd(item, description, quantity);

    setDescription('');
    setQuantity(1);
  }, [description, item, onAdd, quantity]);

  return (
    <Popup onDismiss={onDismiss} size="full" showing={showing}>
      {item && (
        <div className="relative flex flex-col flex-shrink-0 lg:flex-row h-full overflow-scroll">
          <div className="flex flex-col flex-shrink-0 items-center justify-center h-80 lg:h-full lg:w-1/2 bg-white">
            <DismissButton light className="absolute top-3 left-2 lg:hidden" onClick={onDismiss} />
            <DismissButton className="hidden absolute top-4 right-8 lg:block z-10" onClick={onDismiss} />

            <FadeImage
              asBackground
              className="h-full w-full flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
              src={item.coverImageUrl}
            />
          </div>

          <div className="relative flex flex-shrink-0 flex-col mb-40 lg:w-1/2">
            <Divider className="lg:hidden" />
            <p className="font-medium text-2xl lg:text-3xl mx-4 lg:mx-8 mt-8 lg:mt-10">{item.name}</p>
            {item.description && <p className="ml-4 lg:ml-8 mr-8 mt-3 text-gray-400">{item.description}</p>}

            <label className="mx-4 lg:mx-8 mt-10 text-xs lg:text-sm text-gray-900 font-medium tracking-wider uppercase">
              Add description
            </label>
            <Divider className="mt-3 lg:hidden" />
            <textarea
              className="px-4 lg:px-8 pt-3 lg:mt-3 lg:border-t lg:border-b pb-0.5 min-h-30 focus:outline-none resize-none text-gray-900"
              placeholder={
                deliveryType === DeliveryType.request ? 'Color, where you left it, etc...' : 'Color, size, etc...'
              }
              onChange={({ target }) => setDescription(target.value)}
              value={description}
            />
            <Divider className="lg:hidden" />

            <Counter className="mx-auto mt-12 lg:hidden" defaultValue={1} onChange={setQuantity} />
          </div>

          <div className="fixed left-0 lg:left-1/2 bottom-0 right-0 pt-4 z-10 bg-white border-t flex items-center">
            <Counter className="hidden ml-6 mb-4 lg:flex" defaultValue={1} onChange={setQuantity} />
            <MainButton className="mx-6 w-floating mb-6 lg:mb-4" title="Add to Cart" onClick={onConfirm} />
          </div>
        </div>
      )}
    </Popup>
  );
};

export default Item;
